import { useCallback, useEffect, useMemo } from 'react';

import { useManualQuery } from '@zenbusiness/application-commons-graphql';
import dayjs from 'dayjs';

import {
  TOKEN_TYPES,
  CARDS_SENSITIVE_SCOPE,
  CARDS_SENSITIVE_WRITE_SCOPE,
  COMMON_SCOPES,
  MOBILE_CHECK_DEPOSITS_SCOPE
} from '#client/constants/tokens';
import { useLogError } from '#client/hooks/useLogError';
import { GET_CUSTOMER_TOKEN } from '#client/queries';

import useConstBusinessEntityUuid from './useConstBusinessEntityUuid';
import useSessionStorage from './useSessionStorage';
import useToastContext from './useToastContext';
import useUnitAccountId from './useUnitAccountId';

export default function useCustomerToken({ type, prefetch, earlyExpirationMinutes = 0 }) {
  const { unitAccountId, isValidMfa, isEnabled, isEnrolled, isFetchingMfaStatus } =
    useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const scopes = useMemo(() => {
    switch (type) {
      case TOKEN_TYPES.COMMON:
        return COMMON_SCOPES;
      case TOKEN_TYPES.CARDS_SENSITIVE:
        return CARDS_SENSITIVE_SCOPE;
      case TOKEN_TYPES.CARDS_SENSITIVE_WRITE:
        return CARDS_SENSITIVE_WRITE_SCOPE;
      case TOKEN_TYPES.MOBILE_CHECK_DEPOSITS:
        return MOBILE_CHECK_DEPOSITS_SCOPE;
      default:
        throw new Error('Token type is not supported');
    }
  }, [type]);

  const [cachedToken, setToken, getToken] = useSessionStorage(
    `banking.${type}.${businessEntityUuid}.${unitAccountId}`,
    null
  );

  const isSkipQuery =
    isFetchingMfaStatus || !isEnrolled || !isEnabled || !isValidMfa || !unitAccountId;

  const [fetchCustomerToken, { error, loading, errors }] = useManualQuery(GET_CUSTOMER_TOKEN, {
    variables: {
      params: {
        businessEntityUuid,
        scopes
      }
    },
    noThrow: true,
    skipCache: true
  });
  useLogError(errors, error);
  const isValidCustomerToken = useCallback((token) => {
    if (!token) {
      return false;
    }

    const expiresAt = dayjs(token?.expiresAt);
    return expiresAt.isValid() && dayjs().isBefore(expiresAt);
  }, []);

  const fetchCustomerTokenAndSetToken = useCallback(async () => {
    const { data, error: errorToken } = await fetchCustomerToken();
    const { token, expiresIn } = data?.customerToken || {};
    const modifiedExpiresIn = expiresIn - earlyExpirationMinutes * 60;

    if (errorToken || !token || !modifiedExpiresIn) {
      createCommonErrorToast({ error: errorToken });
      setToken(null);
      return null;
    }

    setToken({ token, expiresAt: dayjs().add(modifiedExpiresIn, 'second').toString() });

    return token;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCustomerToken]);

  const getCustomerToken = useCallback(
    async ({ forceRefresh } = {}) => {
      if (!forceRefresh) {
        const latestCachedToken = getToken();
        if (isValidCustomerToken(latestCachedToken)) {
          return latestCachedToken?.token;
        }
      }
      const token = await fetchCustomerTokenAndSetToken();
      return token;
    },
    [fetchCustomerTokenAndSetToken, getToken, isValidCustomerToken]
  );

  useEffect(() => {
    const latestCachedToken = getToken();
    if (latestCachedToken && !isValidCustomerToken(latestCachedToken)) {
      setToken(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shouldFetchCustomerToken =
      !isSkipQuery && !error && !loading && !cachedToken && unitAccountId && prefetch;
    if (shouldFetchCustomerToken) {
      fetchCustomerTokenAndSetToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedToken, unitAccountId, isSkipQuery, error, loading, prefetch]);

  return {
    error,
    loading,
    customerToken: cachedToken?.token,
    getCustomerToken
  };
}
